import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useLessonNarration — browser-native text-to-speech with sentence highlighting.
 *
 * Plays a queue of {id, text} sentences via Web Speech API. On each utterance
 * start, exposes the current sentence id so the lesson UI can karaoke-highlight
 * the active sentence.
 *
 * Returns:
 *   - state: 'idle' | 'playing' | 'paused'
 *   - currentSentenceId: id of the currently-speaking sentence (null when idle)
 *   - voices: filtered list of English voices the user can pick from
 *   - voice: currently selected voice (SpeechSynthesisVoice)
 *   - rate: speech rate (0.5 - 2)
 *   - supported: boolean — false if browser lacks SpeechSynthesis
 *   - play(sentenceList, startIdx?): start playing from index (default 0)
 *   - pause(): pause current utterance
 *   - resume(): resume from pause
 *   - stop(): cancel everything, reset state
 *   - setRate(n): change speech rate
 *   - setVoice(v): change voice
 *   - skipTo(idx): jump to a specific sentence
 */
export function useLessonNarration() {
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [state, setState] = useState("idle");        // 'idle' | 'playing' | 'paused'
  const [currentSentenceId, setCurrentSentenceId] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voice, setVoice] = useState(null);
  const [rate, setRate] = useState(1);

  const sentencesRef = useRef([]);   // active queue
  const indexRef = useRef(0);
  const pausedIndexRef = useRef(0);  // sentence index we were on when paused
  const currentUtterRef = useRef(null);
  const isStoppingRef = useRef(false);

  // Load available voices (async — browsers populate this over time)
  useEffect(() => {
    if (!supported) return;
    const loadVoices = () => {
      const all = window.speechSynthesis.getVoices();
      // Prefer English voices; alphabetize
      const en = all
        .filter((v) => /^en[-_]/i.test(v.lang))
        .sort((a, b) => a.name.localeCompare(b.name));
      setVoices(en.length ? en : all);
      // Default to first US English voice if not yet set
      setVoice((prev) => {
        if (prev) return prev;
        const usDefault =
          en.find((v) => v.lang === "en-US" && v.default) ||
          en.find((v) => v.lang === "en-US") ||
          en[0] ||
          all[0] ||
          null;
        return usDefault;
      });
    };
    loadVoices();
    window.speechSynthesis.addEventListener("voiceschanged", loadVoices);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
  }, [supported]);

  // Stop / cleanup on unmount
  useEffect(() => {
    return () => {
      if (supported) {
        isStoppingRef.current = true;
        window.speechSynthesis.cancel();
      }
    };
  }, [supported]);

  // Speak the sentence at indexRef.current
  const speakCurrent = useCallback(() => {
    if (!supported) return;
    const idx = indexRef.current;
    const list = sentencesRef.current;
    if (idx >= list.length) {
      setState("idle");
      setCurrentSentenceId(null);
      return;
    }
    const s = list[idx];
    const u = new SpeechSynthesisUtterance(s.text);
    if (voice) u.voice = voice;
    u.rate = rate;
    u.pitch = 1;
    u.volume = 1;

    u.onstart = () => {
      setCurrentSentenceId(s.id);
      setState("playing");
    };
    u.onend = () => {
      if (isStoppingRef.current) return;
      indexRef.current = idx + 1;
      speakCurrent();
    };
    u.onerror = (e) => {
      // 'interrupted' / 'canceled' fire when we cancel — that's expected, not an error.
      // Other errors are silently swallowed; narration UI shows idle state.
      if (e.error && e.error !== "interrupted" && e.error !== "canceled") {
        setState("idle");
        setCurrentSentenceId(null);
      }
    };

    currentUtterRef.current = u;
    window.speechSynthesis.speak(u);
  }, [voice, rate, supported]);

  const play = useCallback((sentenceList, startIdx = 0) => {
    if (!supported || !sentenceList?.length) return;
    isStoppingRef.current = true;
    window.speechSynthesis.cancel();        // clear any existing queue
    // The cancel above fires synchronously but engines need a tick; tiny defer
    setTimeout(() => {
      isStoppingRef.current = false;
      sentencesRef.current = sentenceList;
      indexRef.current = Math.max(0, Math.min(startIdx, sentenceList.length - 1));
      pausedIndexRef.current = 0;
      speakCurrent();
    }, 30);
  }, [speakCurrent, supported]);

  const pause = useCallback(() => {
    if (!supported) return;
    // Chrome's native pause()/resume() is unreliable — after ~15 sec the engine
    // silently drops the queue and resume() does nothing. Workaround: cancel and
    // remember the current sentence index, then replay from there on resume.
    if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
      pausedIndexRef.current = indexRef.current;
      isStoppingRef.current = true;
      window.speechSynthesis.cancel();
      setState("paused");
    }
  }, [supported]);

  const resume = useCallback(() => {
    if (!supported) return;
    // Re-speak from the sentence we paused on (current sentence restarts from top
    // — that's correct UX: students who paused mid-sentence usually want to
    // re-hear the whole thing once they tap play again).
    if (sentencesRef.current.length === 0) return;
    isStoppingRef.current = false;
    indexRef.current = pausedIndexRef.current;
    setTimeout(() => speakCurrent(), 30);
  }, [supported, speakCurrent]);

  const stop = useCallback(() => {
    if (!supported) return;
    isStoppingRef.current = true;
    window.speechSynthesis.cancel();
    indexRef.current = 0;
    pausedIndexRef.current = 0;
    sentencesRef.current = [];
    setState("idle");
    setCurrentSentenceId(null);
  }, [supported]);

  const skipTo = useCallback((idx) => {
    const list = sentencesRef.current;
    if (!list.length) return;
    indexRef.current = Math.max(0, Math.min(idx, list.length - 1));
    // Cancel current utterance; the onend handler is guarded by isStoppingRef.
    isStoppingRef.current = true;
    window.speechSynthesis.cancel();
    setTimeout(() => {
      isStoppingRef.current = false;
      speakCurrent();
    }, 30);
  }, [speakCurrent]);

  return {
    state,
    currentSentenceId,
    voices,
    voice,
    setVoice,
    rate,
    setRate,
    supported,
    play,
    pause,
    resume,
    stop,
    skipTo,
  };
}

/**
 * Split a paragraph of lesson body text into sentences for sentence-by-sentence
 * narration. Uses a simple character scanner that explicitly skips decimal points
 * (4.3, 0.01), version numbers (NFPA 72.3), and only splits when terminal
 * punctuation is followed by whitespace + a capital letter or opening paren.
 */
export function splitIntoSentences(text) {
  if (!text) return [];
  const out = [];
  let buf = "";
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    buf += c;
    if (c === "." || c === "!" || c === "?") {
      // Collapse runs of terminal punctuation (e.g. "!!", "...") into one decision point
      while (i + 1 < text.length && (text[i + 1] === "." || text[i + 1] === "!" || text[i + 1] === "?")) {
        buf += text[++i];
      }
      const next1 = text[i + 1];
      const next2 = text[i + 2];
      // End of string → flush
      if (next1 === undefined) {
        out.push(buf.trim());
        buf = "";
        continue;
      }
      // Decimal / version number — don't split (e.g. "4.3", "1.2.3", "0.01")
      if (next1 >= "0" && next1 <= "9") continue;
      // Only split if whitespace + capital letter or '(' follows
      if (/\s/.test(next1) && next2 !== undefined && /[A-Z(]/.test(next2)) {
        out.push(buf.trim());
        buf = "";
      }
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out.filter(Boolean);
}
